/**
 * Component responsible for protecting the routes by using roles and permissions.
 */

import Cookies from 'js-cookie';
import { get, includes, isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import QueryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, withRouter } from 'react-router-dom';
import { withAuthHandler } from './components/common/AuthHandler';
import { TASK_ID_URL_QUERY, TOKEN_NAME } from './config/config';
import { getCurrentUser } from './store/auth/sagas';
import { getUserRole } from './store/roles/sagas';
import { setCurrentRouteAction } from './store/storedRoute/actions';

interface IProps {
    component: any;
    path: string;
    exact?: boolean;
    location: {
        pathname: string;
        search: any;
    };
    allowedRoles?: string[];
    logoutUser: () => void;
    history: {
        goBack: () => void;
    };
}

const ProtectedRoute: React.FC<IProps> = ({
    component: Component,
    allowedRoles,
    exact,
    ...otherProps
}) => {
    const currentUser = useSelector(getCurrentUser);
    const token = Cookies.get(TOKEN_NAME);
    const dispatch = useDispatch();
    const userRole = useSelector(getUserRole);

    const { search } = otherProps.location;
    const urlQueries = QueryString.parse(search);

    const setTaskIdInLocalStorage = () => {
        const taskIdUrlQuery = get(urlQueries, 'id');

        if (taskIdUrlQuery) {
            const existingTaskId = localStorage.getItem(TASK_ID_URL_QUERY);
            if (existingTaskId !== taskIdUrlQuery) {
                localStorage.setItem(
                    TASK_ID_URL_QUERY,
                    taskIdUrlQuery as string
                );
            }
        }
    };

    useEffect(setTaskIdInLocalStorage, [urlQueries]);

    return (
        <Route
            exact={exact}
            render={(props: IProps) => {
                if (!isEmpty(currentUser) && token) {
                    if (!isEmpty(allowedRoles)) {
                        if (!includes(allowedRoles, userRole)) {
                            dispatch(setCurrentRouteAction('/app'));
                            return <Redirect to="/app" />;
                        }
                    }

                    return <Component />;
                } else {
                    const currentRoute = token
                        ? props.location.pathname
                        : '/app';
                    dispatch(setCurrentRouteAction(currentRoute));

                    return <Redirect to="/" />;
                }
            }}
            {...otherProps}
        />
    );
};

export default withRouter(withAuthHandler(ProtectedRoute));
